import deepMerge from 'deepmerge';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

import { types } from '@boss/boss-client';
import { accountMagazineSubscriptionPageSlugs } from '@boss/constants/b2b-b2c';
import { useGeoLocation } from '@boss/hooks';
import { IAccount, IDeliveryPreference } from '@boss/services/client';
import { Alert, Button, Presence, RadioGroup } from '@boss/ui';
import { Coordinates, sortByClosestLocation } from '@boss/utils';

import ContactInfoBlocks from './ContactInfoBlocks';
import {
  useContacts,
  useDeliveryPreferences,
  useSaveSubscription,
  useUpdateAccount,
  useUpdateContact,
} from '../../../client-queries';
import { AccountSkeleton, PickupPointDropdown } from '../../../components';
import { FormValues } from '../../../components/DynamicForm';
import { usePointOfSale, useProfile } from '../../../hooks';
import { isB2b } from '../../../utils';

type Props = {
  account: IAccount;
  className?: string;
};

type DeliveryMethod = 'Delivery' | 'Pickup';

/**
 * Maps the /deliverypreferences response to RadioGroup options. The response is
 * treated as unusable — static Delivery/Pickup fallback — when the request
 * failed, the list is empty, or any item is missing its code.
 */
const getDeliveryMethodOptions = (
  preferences: IDeliveryPreference[] | undefined,
  labels: Record<DeliveryMethod, string>,
): { value: string; label: string }[] => {
  if (!preferences?.length || preferences.some(preference => !preference.code)) {
    return [
      { value: 'Delivery', label: labels.Delivery },
      { value: 'Pickup', label: labels.Pickup },
    ];
  }

  return preferences.map(preference => {
    const value = preference.code as string;
    const staticLabel = value === 'Delivery' ? labels.Delivery : value === 'Pickup' ? labels.Pickup : undefined;

    return { value, label: preference.description || staticLabel || value };
  });
};

const isMissingRequiredShop = (method: DeliveryMethod, shopId: string): boolean => method === 'Pickup' && !shopId;

const getFavouriteShopAsterisk = (isB2bChannel: boolean, method: DeliveryMethod): boolean =>
  !isB2bChannel && method === 'Pickup';

const getShopDropdownClassName = (isB2bChannel: boolean, method: DeliveryMethod): string | undefined =>
  isB2bChannel && method === 'Delivery' ? 'pointer-events-none opacity-50' : undefined;

const getDeliveryPreferenceUpdate = (method: DeliveryMethod, shopId: string): FormValues => ({
  delivery: method,
  ...(method === 'Pickup' ? { shop: shopId } : {}),
});

/**
 * Default afhaalpunt cascade for an Afhaling without a favourite shop:
 * coordinates present → closest store (items missing coords sort last);
 * denied/unavailable → first entry; empty store list → null (caller treats it
 * as a missing shop). An existing favourite is handled by the caller.
 */
const resolveDefaultPickupStoreId = (stores: types.Store[], coordinates: Coordinates | null): string | null => {
  if (!stores.length) {
    return null;
  }

  if (coordinates) {
    return sortByClosestLocation(stores, coordinates)[0]?.id ?? stores[0].id;
  }

  return stores[0].id;
};

type UseDefaultPickupStoreParams = {
  stores: types.Store[];
  hasSelectedShop: boolean;
  onStoreResolved: (storeId: string) => void;
  onStoreMissing: () => void;
};

/**
 * Owns the default-afhaalpunt resolution: single-flight, aborts silently when
 * the customer switched back to Levering while resolving, and delegates the
 * selection decision to `resolveDefaultPickupStoreId`. Preselects locally via
 * the callbacks — nothing is persisted here.
 */
const useDefaultPickupStore = ({
  stores,
  hasSelectedShop,
  onStoreResolved,
  onStoreMissing,
}: UseDefaultPickupStoreParams) => {
  const { permissionDenied, fetchGeolocation } = useGeoLocation();
  const [pickupStoreRequested, setPickupStoreRequested] = useState(false);
  const latestDeliveryMethod = useRef<DeliveryMethod | null>(null);
  const pickupResolutionInFlight = useRef(false);

  useEffect(() => {
    if (!pickupStoreRequested || pickupResolutionInFlight.current) {
      return;
    }

    pickupResolutionInFlight.current = true;

    const resolveStore = async () => {
      let coordinates: Coordinates | null = null;

      if (stores.length && !permissionDenied) {
        coordinates = (await fetchGeolocation()) ?? null;
      }

      const storeId = resolveDefaultPickupStoreId(stores, coordinates);

      pickupResolutionInFlight.current = false;
      setPickupStoreRequested(false);

      if (latestDeliveryMethod.current !== 'Pickup') {
        return;
      }

      if (storeId) {
        onStoreResolved(storeId);

        return;
      }

      onStoreMissing();
    };

    resolveStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupStoreRequested, stores, permissionDenied, fetchGeolocation]);

  const startDefaultPickupStoreResolution = (method: DeliveryMethod) => {
    latestDeliveryMethod.current = method;

    if (method === 'Pickup' && !hasSelectedShop) {
      setPickupStoreRequested(true);
    }
  };

  return { startDefaultPickupStoreResolution };
};

type DeliveryPreferenceSectionProps = {
  account: IAccount;
  stores: types.Store[];
  deliveryPreferences: IDeliveryPreference[] | undefined;
  onConfirm: (values: FormValues) => void;
  isSaving: boolean;
};

/**
 * Levermethode radio + favourite afhaalpunt dropdown with the deferred (confirm
 * button) save model. Selections stay local; the parent persists them via
 * `onConfirm` when the customer confirms.
 */
const DeliveryPreferenceSection = ({
  account,
  stores,
  deliveryPreferences,
  onConfirm,
  isSaving,
}: DeliveryPreferenceSectionProps) => {
  const { t } = useTranslation(['account', 'forms']);
  const searchOption = t('info.option');
  const searchLabel = t('info.searchLabel');

  // Display-only fallback: a customer without a saved method sees "Levering".
  // Radio and shop selections stay local until the confirmation button is pressed.
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>(account.preference?.delivery ?? 'Delivery');
  const [selectedShopId, setSelectedShopId] = useState(account.preference?.shop ?? '');
  const [pickupStoreError, setPickupStoreError] = useState(false);

  const { startDefaultPickupStoreResolution } = useDefaultPickupStore({
    stores,
    hasSelectedShop: Boolean(selectedShopId),
    onStoreResolved: storeId => {
      setSelectedShopId(storeId);
      setPickupStoreError(false);
    },
    onStoreMissing: () => setPickupStoreError(true),
  });

  const handleDeliveryMethodChange = (value: string) => {
    const method: DeliveryMethod = value === 'Pickup' ? 'Pickup' : 'Delivery';

    setDeliveryMethod(method);
    startDefaultPickupStoreResolution(method);

    if (!isMissingRequiredShop(method, selectedShopId)) {
      setPickupStoreError(false);
    }
  };

  const handleShopChange = (shopId: string) => {
    setSelectedShopId(shopId);
    setPickupStoreError(false);
  };

  const handleConfirm = () => {
    if (isMissingRequiredShop(deliveryMethod, selectedShopId)) {
      setPickupStoreError(true);

      return;
    }

    setPickupStoreError(false);
    onConfirm(getDeliveryPreferenceUpdate(deliveryMethod, selectedShopId));
  };

  return (
    <>
      <h4 className="mt-15 mb-3">{t('info.deliveryPreference.title')}</h4>
      <RadioGroup
        name="deliveryPreference"
        onChange={handleDeliveryMethodChange}
        options={getDeliveryMethodOptions(deliveryPreferences, {
          Delivery: t('info.deliveryPreference.delivery'),
          Pickup: t('info.deliveryPreference.pickup'),
        })}
        value={deliveryMethod}
      />
      <h4 className="mt-15 mb-3">
        {t('info.favorite')}
        {getFavouriteShopAsterisk(isB2b, deliveryMethod) && <span className="text-red-500">*</span>}
      </h4>
      <PickupPointDropdown
        className={getShopDropdownClassName(isB2b, deliveryMethod)}
        error={pickupStoreError}
        favouriteShopId={account.preference?.shop}
        onChange={store => handleShopChange(store.id)}
        searchLabel={searchLabel}
        selectLabel={searchOption}
        stores={stores}
        value={selectedShopId}
      />
      {pickupStoreError && <p className="mt-1 text-sm text-red-500">{t('info.deliveryPreference.requiredShop')}</p>}
      <Button
        className="mt-5"
        disabled={isSaving}
        label={t('info.deliveryPreference.confirm')}
        onClick={handleConfirm}
        type="primary"
      />
    </>
  );
};

const AccountInfoPage = ({ account, className }: Props) => {
  const { t } = useTranslation(['account', 'forms']);
  const { locale, push } = useRouter();
  const { stores, isLoading: storesLoading } = usePointOfSale(3);
  const { data: contacts, isLoading: contactsLoading } = useContacts(locale as string);
  const { data: profile, status: profileStatus } = useProfile();
  const { data: deliveryPreferences } = useDeliveryPreferences(locale as string);

  const { mutate: updateAccount, isError: updateAccountError, isLoading: updateAccountLoading } = useUpdateAccount();
  const { mutate: updateContact, isError: updateContactError, isLoading: updateContactLoading } = useUpdateContact();
  const { mutate: saveSubscription, isLoading: saveSubscriptionLoading } = useSaveSubscription();

  const profileLoading = profileStatus !== 'idle';

  const [subscribedToNewsletter, setSubscribedToNewsletter] = useState(account?.preference?.onlinecommunication);
  const [subscribedToMagazine, setSubscribedToMagazine] = useState(account?.preference?.offlinecommunication);

  //Beware! different id is needed depending on b2c/b2b
  const contactInfo = contacts?.find(
    contact => contact.id === (isB2b ? profile?.extension_ContactPersonId : profile?.extension_AccountId),
  );

  const toggleNewsletterSubscription = () => {
    if (contactInfo?.email) {
      saveSubscription(
        {
          email: contactInfo?.email,
          subscribeToNewsletter: !subscribedToNewsletter,
          subscribeToMagazine: subscribedToMagazine,
          successMessage: t(
            !subscribedToNewsletter ? 'toast.saveSubscription.success.title' : 'toast.deleteSubscription.success.title',
            {
              ns: 'common',
            },
          ) as string,
        },
        {
          onSuccess: () => setSubscribedToNewsletter(!subscribedToNewsletter),
        },
      );
    }
  };

  const toggleMagazineSubscription = () => {
    if (subscribedToMagazine) {
      if (contactInfo?.email) {
        saveSubscription(
          {
            email: contactInfo?.email,
            subscribeToMagazine: false,
            successMessage: t('toast.deleteSubscription.success.title', {
              ns: 'common',
            }) as string,
          },
          {
            onSuccess: () => setSubscribedToMagazine(false),
          },
        );
      }
    } else {
      push(`/${accountMagazineSubscriptionPageSlugs[locale as keyof typeof accountMagazineSubscriptionPageSlugs]}`);
    }
  };

  const updateAccountInfo = (id: string, values: FormValues) => {
    let fieldsToUpdate = {};

    if (id === 'info') {
      fieldsToUpdate = {
        ...contactInfo,
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
        mobilephonenumber: values.mobilephonenumber,
        officephonenumber: values.officephonenumber,
      };

      if (contactInfo) {
        updateContact({
          id: contactInfo?.id,
          accountnumber: account.accountnumber,
          firstname: values.firstname?.toString(),
          lastname: values.lastname?.toString(),
          email: values.email?.toString(),
          mobilephonenumber: values.mobilephonenumber?.toString(),
          officephonenumber: values.phonenumber?.toString(),
        });
      }
      return;
    }

    if (id === 'deliveryAddr' || id === 'invoiceAddr') {
      const addressType = id === 'invoiceAddr' ? 'invoice' : 'delivery';

      const accountAdjustmentObject = values.name
        ? {
            name: values.name,
          }
        : {};

      fieldsToUpdate = {
        accountinfo: {
          ...account.accountinfo,
          ...accountAdjustmentObject,
        },
        addresses: [
          ...account.addresses.filter(address => address.type !== addressType),
          {
            type: addressType,
            street: values.street,
            streetnumber: values.streetnumber,
            zipcode: values.zipcode,
            postbox: values.postbox,
            city: values.city,
            country: values.country,
          },
        ],
        billinginfo: {
          ...account.billinginfo,
          vatnumber: values.vatnumber || account.billinginfo?.vatnumber,
          companyname: values.companyname,
        },
      };
    }

    if (id === 'preferenceDelivery') {
      fieldsToUpdate = {
        preference: {
          ...account.preference,
          delivery: values.delivery,
          // Only pass the shop along when confirming a Pickup; the spread above
          // keeps the saved favourite untouched otherwise.
          ...(values.shop ? { shop: values.shop } : {}),
        },
      };
    }

    const overwriteMerge = (_: [], sourceArray: []) => sourceArray;

    updateAccount(deepMerge(account, fieldsToUpdate, { arrayMerge: overwriteMerge }));
  };

  return (
    <Presence
      id="account-page"
      isLoading={storesLoading || contactsLoading || profileLoading}
      loader={<AccountSkeleton />}
      visible
    >
      {stores && (
        <div className={className}>
          <h1 className="mb-3">{t('info.personal')}</h1>
          {!updateAccountLoading && !updateContactLoading && (updateAccountError || updateContactError) && (
            <Alert className="my-2" type="error">
              {t('info.updateError')}
            </Alert>
          )}
          <ContactInfoBlocks account={account} contactInfo={contactInfo} onUpdate={updateAccountInfo} />
          <DeliveryPreferenceSection
            account={account}
            deliveryPreferences={deliveryPreferences}
            isSaving={updateAccountLoading}
            onConfirm={values => updateAccountInfo('preferenceDelivery', values)}
            stores={stores}
          />

          {!isB2b && (
            <>
              <h4 className="mt-15 mb-3">
                {t('info.coloraMagazine')}: {subscribedToMagazine ? t('info.subscribed') : t('info.notSubscribed')}
              </h4>
              <Button
                disabled={saveSubscriptionLoading}
                label={subscribedToMagazine ? t('info.unsubscribe') : t('info.subscribe')}
                onClick={toggleMagazineSubscription}
                type="link"
              />
              {contactInfo?.email && (
                <>
                  <h4 className="mt-15 mb-3">
                    {t('info.newsletter')}: {subscribedToNewsletter ? t('info.subscribed') : t('info.notSubscribed')}
                  </h4>
                  {!subscribedToNewsletter && (
                    <p className="pb-5 text-sm">{t('fields.subscribeNewsletterIncentive', { ns: 'forms' })}</p>
                  )}
                  <Button
                    disabled={saveSubscriptionLoading}
                    label={subscribedToNewsletter ? t('info.unsubscribe') : t('info.subscribe')}
                    onClick={toggleNewsletterSubscription}
                    type="link"
                  />
                </>
              )}
            </>
          )}
        </div>
      )}
    </Presence>
  );
};

export default AccountInfoPage;
